import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children"],
  _excluded2 = ["pageCount", "defaultPage", "disabledPageTooltipTitle", "enabledPageLimit", "maxVisiblePageButtons", "onPageChange", "page", "showButtonLabels", "showFirstLastButtons", "showLastPageNumber"];
import { DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE } from '../../constants';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { Flex } from '../../../components/Flex/Flex';
import { Button } from '../Button/Button';
import partial from 'react-utils/partial';
import { getMinAndMaxPageNumbers } from './Paginator.helpers';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'foundations-theming/utils';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { filterProps } from '../../../utils/filterProps';
import { Controllable } from '../Controllable';
import { Tooltip } from '../Tooltip';
import { ScreenReaderOnly } from '../ScreenReaderOnly';
import { ControlProvider } from '../Link/ControlContext';
import { EllipsesIcon } from 'foundations-assets/icons/components/EllipsesIcon';
import { FirstIcon } from 'foundations-assets/icons/components/FirstIcon';
import { LastIcon } from 'foundations-assets/icons/components/LastIcon';
import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
const StyledNav = styled.nav.withConfig({
  displayName: "Paginator__StyledNav"
})(({
  $cssStyles,
  theme
}) => {
  return Object.assign({
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'row nowrap',
    justifyContent: 'center',
    '& button': {
      padding: `5px 6px`,
      // Since Button component doesn't allow us to set active state and can't be
      // styled with Styled Components, we have to use data-active attribute
      '&[data-active="true"]': {
        backgroundColor: theme.color['fill-field-hover'],
        border: `${theme.borderWidth['100']} solid ${theme.color['border-interactive-default']}`,
        '&:hover': {
          backgroundColor: theme.color['fill-field-hover'],
          border: `${theme.borderWidth['100']} solid ${theme.color['border-interactive-default']}`
        }
      },
      '&[aria-disabled="true"]': {
        color: theme.color['text-interactive-default-disabled']
      }
    }
  }, $cssStyles);
});
const NumberSpan = styled.span.withConfig({
  displayName: "Paginator__NumberSpan"
})(() => {
  return {
    display: 'flex',
    justifyContent: 'center',
    ['@media only screen and (max-width: 480px)']: {
      display: 'none'
    }
  };
});
const StyledButton = _ref => {
  let {
      children
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(Button, Object.assign({
    size: "medium",
    use: "transparent",
    responsive: false
  }, rest, {
    children: children
  }));
};
const PaginatorComponent = _ref2 => {
  let {
      pageCount,
      disabledPageTooltipTitle,
      enabledPageLimit,
      maxVisiblePageButtons = 11,
      onPageChange,
      page = 1,
      showButtonLabels = true,
      showFirstLastButtons = false,
      showLastPageNumber
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const goToPage = newPage => {
    if (newPage === page) return;
    onPageChange === null || onPageChange === void 0 || onPageChange(syntheticEvent(newPage));
  };

  // Wrap in tooltip component when it is available
  const renderPageNumber = ({
    pageNumber,
    isActive,
    key
  }) => {
    return /*#__PURE__*/_jsx(Tooltip, {
      disabled: enabledPageLimit == null || enabledPageLimit != null && pageNumber <= enabledPageLimit,
      title: disabledPageTooltipTitle,
      children: /*#__PURE__*/_createElement(StyledButton, Object.assign({}, isActive ? {
        'aria-current': true
      } : null, {
        key: key !== null && key !== void 0 ? key : pageNumber,
        "data-active": isActive,
        "data-page-number": pageNumber,
        disabled: enabledPageLimit != null && pageNumber > enabledPageLimit,
        onClick: partial(goToPage, pageNumber)
      }), /*#__PURE__*/_jsxs(ScreenReaderOnly, {
        children: [isActive ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "salesUI.UIPaginator.pageNumber.currentIndicator"
        }) : null, /*#__PURE__*/_jsx(FormattedMessage, {
          message: "salesUI.UIPaginator.pageNumber.description"
        })]
      }), pageNumber)
    }, key !== null && key !== void 0 ? key : pageNumber);
  };

  // Returns an array of page number StyledButtons to be rendered
  const renderNumberedPages = () => {
    const [min, max] = getMinAndMaxPageNumbers(maxVisiblePageButtons, page, pageCount);
    const links = [];
    for (let currentPage = min; currentPage <= max; currentPage++) {
      const isActive = currentPage === page;
      links.push(renderPageNumber({
        isActive,
        pageNumber: currentPage,
        // Use relative position as key instead of absolute page number to maintain stable
        // React keys when pagination range shifts, preventing bouncy animations
        key: currentPage - min
      }));
    }
    return links;
  };

  // Renders the last page number button
  const renderLastPageNumber = () => {
    const [min, max] = getMinAndMaxPageNumbers(maxVisiblePageButtons, page, pageCount);
    if (maxVisiblePageButtons < 1 || pageCount < 1 || pageCount === Infinity || pageCount >= min && pageCount <= max) {
      return null;
    }
    return /*#__PURE__*/_jsxs(ControlProvider, {
      isControl: true,
      children: [pageCount !== max + 1 && /*#__PURE__*/_jsx(Flex, {
        paddingInline: "200",
        paddingBlock: "100",
        alignItems: "center",
        children: /*#__PURE__*/_jsx(EllipsesIcon, {
          size: "600"
        })
      }), renderPageNumber({
        pageNumber: pageCount
      })]
    });
  };
  return /*#__PURE__*/_jsx(ControlProvider, {
    isControl: true,
    children: /*#__PURE__*/_jsxs(StyledNav, Object.assign({
      "aria-label": unescapedText('foundations-components.Paginator.label'),
      "data-current-page": page,
      role: "navigation",
      $cssStyles: cssStyles
    }, domProps, {
      [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: 'Paginator',
      children: [/*#__PURE__*/_jsxs("span", {
        children: [showFirstLastButtons && /*#__PURE__*/_jsxs(StyledButton, {
          "aria-label": unescapedText('foundations-components.Paginator.firstPage.description'),
          "data-first-page": true,
          disabled: page < 2,
          onClick: partial(goToPage, 1),
          children: [/*#__PURE__*/_jsx(FirstIcon, {
            size: "300"
          }), showButtonLabels && /*#__PURE__*/_jsx(FormattedMessage, {
            message: "foundations-components.Paginator.firstPage.label"
          })]
        }, "first"), /*#__PURE__*/_jsxs(StyledButton, {
          "aria-label": unescapedText('foundations-components.Paginator.previousPage.description'),
          "data-prev-page": true,
          disabled: page < 2,
          onClick: partial(goToPage, Math.max(page - 1, 1)),
          children: [/*#__PURE__*/_jsx(LeftIcon, {
            size: "300"
          }), showButtonLabels && /*#__PURE__*/_jsx(FormattedMessage, {
            message: "foundations-components.Paginator.previousPage.label"
          })]
        }, "prev")]
      }), /*#__PURE__*/_jsxs(NumberSpan, {
        children: [renderNumberedPages(), showLastPageNumber && renderLastPageNumber()]
      }), /*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsxs(StyledButton, {
          "aria-label": unescapedText('foundations-components.Paginator.nextPage.description'),
          "data-next-page": true,
          disabled: page >= pageCount || enabledPageLimit != null && page >= enabledPageLimit,
          onClick: partial(goToPage, Math.min(page + 1, pageCount)),
          children: [showButtonLabels && /*#__PURE__*/_jsx(FormattedMessage, {
            message: "foundations-components.Paginator.nextPage.label"
          }), /*#__PURE__*/_jsx(RightIcon, {
            size: "300"
          })]
        }, "next"), showFirstLastButtons && /*#__PURE__*/_jsxs(StyledButton, {
          "aria-label": unescapedText('foundations-components.Paginator.lastPage.description'),
          "data-last-page": true,
          disabled: enabledPageLimit != null ? page >= enabledPageLimit : pageCount === page,
          onClick: partial(goToPage, enabledPageLimit != null ? enabledPageLimit : pageCount),
          children: [showButtonLabels && /*#__PURE__*/_jsx(FormattedMessage, {
            message: "foundations-components.Paginator.lastPage.label"
          }), /*#__PURE__*/_jsx(LastIcon, {
            size: "300"
          })]
        }, "last")]
      })]
    }))
  });
};
const defaultProps = {
  page: 1
};
export const Paginator = Controllable(PaginatorComponent, ['page'], defaultProps);
Paginator.displayName = 'Paginator';